/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useRef, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import IconBox from "components/Icons/IconBox";
import { Scrollbars } from "react-custom-scrollbars-2";
import {
  renderThumbDark,
  renderThumbLight,
  renderTrack,
  renderTrackRTL,
  renderView,
  renderViewRTL,
} from "components/Scrollbar/Scrollbar";
import { HSeparator } from "components/Separator/Separator";
import { logout, getCurrentUser } from "services/authService";

// ✅ Logolar
import LogoColored from "assets/svg/logo-colored.svg";
import LogoWhite from "assets/svg/logo-white.svg";

function SidebarContent({ routes = [], sidebarVariant, onClose, onToggle, isCollapsed = false }) {
  const location = useLocation();
  const variantChange = "0.2s linear";
  const [openCollapses, setOpenCollapses] = useState({});
  const categoryTextColor = useColorModeValue("gray.700", "white");
  const [userRole, setUserRole] = useState(null);
  const [userBayiTipi, setUserBayiTipi] = useState(null);

  // Kullanıcı rolünü ve bayi_tipi'ni yükle
  useEffect(() => {
    getCurrentUser()
      .then((user) => {
        setUserRole(user.role);
        setUserBayiTipi(user.bayi_tipi);
      })
      .catch(() => {
        setUserRole(null);
        setUserBayiTipi(null);
      });
  }, []);

  const activeRoute = (routeName) => (location.pathname === routeName ? "active" : "");

  // Location değiştiğinde, aktif olmayan collapse'ları kapat
  useEffect(() => {
    const currentPath = location.pathname;
    setOpenCollapses((prev) => {
      const newState = { ...prev };
      // Tüm collapse'ları kontrol et
      routes.forEach((route) => {
        if (route.collapse && route.views) {
          const isChildActive = route.views.some(
            (child) => currentPath === (child.layout + child.path)
          );
          // Eğer bu collapse'ın hiçbir child'ı aktif değilse, kapat
          if (!isChildActive) {
            newState[route.name] = false;
          }
        }
      });
      return newState;
    });
  }, [location.pathname, routes]);

  const toggleCollapse = (name) => {
    setOpenCollapses((prev) => {
      const newState = { ...prev };
      // Diğer collapse'ları kapat, sadece tıklananı aç/kapat
      Object.keys(newState).forEach((key) => {
        if (key !== name) {
          newState[key] = false;
        }
      });
      newState[name] = !prev[name];
      return newState;
    });
  };

  const createLinks = (linkRoutes) => {
    if (!Array.isArray(linkRoutes)) return null;
    return linkRoutes.map((prop, key) => {
      if (prop.redirect) return null;
      
      // Admin-only route kontrolü
      if (prop.adminOnly && userRole !== "admin") {
        return null;
      }
      
      // Role-based route kontrolü (allowedRoles varsa)
      if (prop.allowedRoles && userRole && !prop.allowedRoles.includes(userRole)) {
        return null;
      }
      
      // Bayi tipi kontrolü: Stok ve Sim yönetimi sadece saas_bayi görebilir
      // Teknik personel SIM yönetimini göremez
      if (prop.path === "/stok-yonetimi" || prop.path === "/sim-yonetimi") {
        if (userRole === "bayi" && userBayiTipi !== "saas_bayi") {
          return null;
        }
        if (userRole === "teknik_eleman" && prop.path === "/sim-yonetimi") {
          return null;
        }
      }

      // Kategori başlığı (sadece text, örn. ACCOUNT)
      if (prop.category) {
        return (
          <React.Fragment key={key}>
            {!isCollapsed && (
              <Text
                color={categoryTextColor}
                fontWeight="bold"
                mb="6px"
                ps="16px"
                py="12px"
              >
                {prop.name}
              </Text>
            )}
            {createLinks(prop.views)}
          </React.Fragment>
        );
      }

      // Açılır (collapsible) grup – örn. CRM (Destek)
      if (prop.collapse && Array.isArray(prop.views)) {
        // Admin-only collapse kontrolü
        const hasAdminOnlyViews = prop.views.some((view) => view.adminOnly);
        if (hasAdminOnlyViews && userRole !== "admin") {
          return null;
        }
        
        // Role-based collapse kontrolü
        if (prop.allowedRoles && userRole && !prop.allowedRoles.includes(userRole)) {
          return null;
        }
        
        const isChildActive = prop.views.some(
          (child) => activeRoute(child.layout + child.path) === "active"
        );
        const isOpen = openCollapses[prop.name] || isChildActive;

        return (
          <Box key={key} mb="4">
            <Button
              justifyContent={isCollapsed ? "center" : "flex-start"}
              alignItems="center"
              bg={isChildActive ? "white" : "transparent"}
              boxShadow={
                isChildActive ? "0px 7px 11px rgba(0, 0, 0, 0.04)" : "none"
              }
              ps={isCollapsed ? "0" : "16px"}
              py="12px"
              borderRadius="15px"
              w="100%"
              _hover={{ bg: "none" }}
              _active={{ bg: "inherit", transform: "none", borderColor: "transparent" }}
              _focus={{ boxShadow: "none" }}
              onClick={() => !isCollapsed && toggleCollapse(prop.name)}
              cursor={isCollapsed ? "not-allowed" : "pointer"}
              opacity={isCollapsed ? 0.7 : 1}
            >
              <Flex align="center" w="100%" justify={isCollapsed ? "center" : "space-between"}>
                <Flex align="center">
                  {prop.icon && (
                    <IconBox
                      bg={isChildActive ? "blue.500" : "white"}
                      color={isChildActive ? "white" : "blue.500"}
                      h="30px"
                      w="30px"
                      me={isCollapsed ? "0" : "12px"}
                      transition={variantChange}
                    >
                      {prop.icon}
                    </IconBox>
                  )}
                  {!isCollapsed && (
                    <Text
                      color={isChildActive ? "gray.700" : "gray.400"}
                      my="auto"
                      fontSize="sm"
                      fontWeight="600"
                    >
                      {prop.name}
                    </Text>
                  )}
                </Flex>
                {!isCollapsed && (
                  <Text fontSize="xs" color="gray.400">
                    {isOpen ? "−" : "+"}
                  </Text>
                )}
              </Flex>
            </Button>
            {isOpen && !isCollapsed && (
              <Box mt="2" ms="8">
                {createLinks(
                  prop.views.filter((view) => {
                    // Admin-only kontrolü
                    if (view.adminOnly && userRole !== "admin") return false;
                    // Role-based kontrolü
                    if (view.allowedRoles && userRole && !view.allowedRoles.includes(userRole)) return false;
                    return true;
                  })
                )}
              </Box>
            )}
          </Box>
        );
      }

      return (
        <NavLink to={prop.layout + prop.path} key={key}>
          <Button
            justifyContent={isCollapsed ? "center" : "flex-start"}
            alignItems="center"
            bg={activeRoute(prop.layout + prop.path) === "active" ? "white" : "transparent"}
            boxShadow={
              activeRoute(prop.layout + prop.path) === "active"
                ? "0px 7px 11px rgba(0, 0, 0, 0.04)"
                : "none"
            }
            mb="6px"
            ps={isCollapsed ? "0" : "16px"}
            py="12px"
            borderRadius="15px"
            w="100%"
            _hover={{ bg: "none" }}
            _active={{ bg: "inherit", transform: "none", borderColor: "transparent" }}
            _focus={{ boxShadow: "none" }}
            onClick={onClose}
          >
            <Flex>
              {typeof prop.icon === "string" ? (
                prop.icon
              ) : (
                <IconBox
                  bg={activeRoute(prop.layout + prop.path) === "active" ? "blue.500" : "white"}
                  color={activeRoute(prop.layout + prop.path) === "active" ? "white" : "blue.500"}
                  h="30px"
                  w="30px"
                  me={isCollapsed ? "0" : "12px"}
                  transition={variantChange}
                >
                  {prop.icon}
                </IconBox>
              )}
              {!isCollapsed && (
                <Text
                  color={activeRoute(prop.layout + prop.path) === "active" ? "gray.700" : "gray.400"}
                  my="auto"
                  fontSize="sm"
                >
                  {prop.name}
                </Text>
              )}
            </Flex>
          </Button>
        </NavLink>
      );
    });
  };

  const sidebarBg = useColorModeValue("white", "navy.800");
  const logoImage = useColorModeValue(LogoColored, LogoWhite);
  const collapseButtonColor = useColorModeValue("gray.600", "gray.300");
  const collapseButtonHoverBg = useColorModeValue("gray.100", "gray.700");

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      window.location.href = "/login";
    }
  };

  // Logo bölümü - collapsed modda sadece sol kısmı göster
  const brand = (
    <Box pt="25px" mb="12px">
      <Box
        h="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        transition="all 0.3s ease"
      >
        <Image
          src={logoImage}
          alt="ixPilot Logo"
          h="40px"
          objectFit="contain"
          transition="all 0.3s ease"
          style={isCollapsed ? {
            marginLeft: '-22px',
            maxWidth: '60px'
          } : {}}
        />
      </Box>
      {!isCollapsed && <HSeparator my="26px" />}
    </Box>
  );

  return (
    <Box
      bg={sidebarBg}
      transition="width 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease"
      w={isCollapsed ? "80px" : "260px"}
      maxW={isCollapsed ? "80px" : "260px"}
      h="100%"
      ps={isCollapsed ? "10px" : "20px"}
      pe={isCollapsed ? "10px" : "20px"}
      borderRadius="20px"
      filter="drop-shadow(0px 5px 14px rgba(0, 0, 0, 0.05))"
      position="relative"
      overflow="visible"
    >
      <Scrollbars
        autoHide
        renderTrackVertical={document.documentElement.dir === "rtl" ? renderTrackRTL : renderTrack}
        renderThumbVertical={useColorModeValue(renderThumbLight, renderThumbDark)}
        renderView={document.documentElement.dir === "rtl" ? renderViewRTL : renderView}
      >
        <Box>{brand}</Box>
        <Stack direction="column" mb="40px">
          <Box>{createLinks(routes)}</Box>
        </Stack>
        {!isCollapsed && (
          <Box
            mb="22px"
            mt="auto"
            px="4"
            display="flex"
            justifyContent="center"
          >
            <Button
              colorScheme="red"
              variant="outline"
              size="sm"
              fontSize="sm"
              h="32px"
              w="auto"
              px="20px"
              onClick={handleLogout}
            >
              Çıkış Yap
            </Button>
          </Box>
        )}
      </Scrollbars>
      
      {/* Sidebar'ın sağ tarafına entegre collapse butonu */}
      {onToggle && (
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          transform="translateY(-50%)"
          zIndex={1002}
          display={{ base: "none", xl: "block" }}
          opacity={1}
          transition="opacity 0.2s ease 0.1s"
        >
          <IconButton
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
            size="sm"
            w="28px"
            h="56px"
            minW="28px"
            borderRadius="0 8px 8px 0"
            bg={sidebarBg}
            color={collapseButtonColor}
            border="none"
            boxShadow="-2px 0 8px rgba(0, 0, 0, 0.08)"
            _hover={{ 
              bg: collapseButtonHoverBg,
              boxShadow: "-2px 0 12px rgba(0, 0, 0, 0.12)",
            }}
            transition="all 0.2s ease"
            onClick={onToggle}
          />
        </Box>
      )}
    </Box>
  );
}

// ✅ Masaüstü Sidebar
function Sidebar(props) {
  const mainPanel = useRef();
  const { sidebarVariant, routes = [], isOpen = true, onToggle } = props;
  const isCollapsed = !isOpen; // isOpen=false => collapsed=true

  return (
    <Box ref={mainPanel} position="relative">
      <Box
        display={{ sm: "none", xl: "block" }}
        position="fixed"
        h="100vh"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        zIndex={1000}
      >
        <SidebarContent 
          routes={routes} 
          sidebarVariant={sidebarVariant} 
          onToggle={onToggle}
          isCollapsed={isCollapsed}
        />
      </Box>
    </Box>
  );
}

// ✅ Mobil (Drawer) SidebarResponsive
export function SidebarResponsive(props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { sidebarVariant, routes = [] } = props;

  return (
    <>
      <IconButton
        aria-label="Open menu"
        icon={<HamburgerIcon />}
        variant="ghost"
        onClick={onOpen}
        display={{ base: "block", xl: "none" }}
        ms="4"
      />
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p="0">
            <SidebarContent routes={routes} sidebarVariant={sidebarVariant} onClose={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default Sidebar;
